import React, { useEffect, useRef, useState } from "react";
import { Box, Table, Thead, Tbody, Tr, Th, Td, Select, Spinner, useColorModeValue } from "@chakra-ui/react";
import axios from "axios";

const TableComment = ({ selectedRetailer, selectedSupplier, ddStrikeRate, weekEndingDate, comment, search, onResolvedWeek }) => {
  const [TheadData, setTheadData] = useState([]);
  const [TbodyData, setTbodyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const theadBg = useColorModeValue("gray.50", "gray.700");

  // Which week the last completed response actually rendered, tagged with the
  // retailer/supplier it belongs to so a switch cannot carry a stale week forward.
  // Needed because sending an empty week means "server, pick the latest" — until the
  // response lands we do not know what we asked for.
  const resolvedRef = useRef(null);
  // Identifies the data on screen (or in flight). Without it the parent adopting the
  // resolved week — which happens moments after the first response — would re-request
  // rows we already have.
  const lastKeyRef = useRef(null);
  // Drops responses from a request that a retailer/supplier switch has superseded.
  const reqIdRef = useRef(0);

  const keyOf = (wed) => [selectedRetailer, selectedSupplier, wed, comment, search].join("|");

  const fetchTableComment = async (wed, cmt, srch) => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/rep_strikeRate/get-table-comment`, {
        retailer: selectedRetailer,
        supplier: selectedSupplier,
        week_ending_date: wed,
        comment: cmt,
        search: srch,
      });
      if (myReq !== reqIdRef.current) return; // a newer request is already in flight
      const resolved = res.data.data.week_ending_date || wed || "";
      resolvedRef.current = { retailer: selectedRetailer, supplier: selectedSupplier, week: resolved };
      // Re-key on the week the server actually used, so the parent handing us that same
      // week back is recognised as "already have it" rather than a new request.
      lastKeyRef.current = keyOf(resolved);
      setTheadData(res.data.data.tableData.thead);
      setTbodyData(res.data.data.tableData.tbody);
      setLoading(false);
      if (!wed && resolved && onResolvedWeek) onResolvedWeek(resolved);
    } catch (error) {
      if (myReq !== reqIdRef.current) return;
      console.error("Error fetching comment table data:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fires on mount, before the week dropdown has loaded — an empty week asks the server
    // for the latest one. This is what moves the line detail out of the third request wave
    // and into the first.
    const known =
      resolvedRef.current &&
      resolvedRef.current.retailer === selectedRetailer &&
      resolvedRef.current.supplier === selectedSupplier
        ? resolvedRef.current.week
        : "";
    const key = keyOf(weekEndingDate || known);
    if (key === lastKeyRef.current) return;
    lastKeyRef.current = key;
    fetchTableComment(weekEndingDate, comment, search);
  }, [selectedRetailer, selectedSupplier, weekEndingDate, comment, search]);

  const postReason = async (row) => {
    const payload = {
      retailer: selectedRetailer,
      supplier: selectedSupplier,
      week_ending_date: row.week_ending_date,
      site_code: row.site_code,
      article_code: row.article_code,
      po_code: row.po_code,
      reason_uuid: row.reason_uuid,
    };
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/rep_strikeRate/save-reason`, payload);
      if (!res.data.status) {
        console.warn("Failed to update reason. Response:", res);
      }
    } catch (error) {
      console.error("Error updating reason:", error);
    }
  };

  const handleReasonChange = async (e, row) => {
    const reason_uuid = e.target.value;
    const updatedRow = { ...row, reason_uuid };
    await postReason(updatedRow);
    // po_code included: one article/store/week can carry several PO lines, each with its own reason
    setTbodyData((prevData) => prevData.map((r) => (r.week_ending_date === row.week_ending_date && r.site_code === row.site_code && r.article_code === row.article_code && r.po_code === row.po_code ? updatedRow : r)));
  };

  function formatDateString(dateString) {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const date = new Date(dateString);
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear().toString().slice(-2);
    return `${day} ${month} '${year}`;
  }

  if (loading) return <Spinner />;

  return (
    <Box overflowY="auto" maxH="calc(70vh - 60px)">
      <Table variant="striped" size="sm" colorScheme="custom">
        <Thead position="sticky" top={0} zIndex={1} bg={theadBg}>
          {TheadData.map((row, index) => (
            <Tr key={index}>
              <Th fontSize="xs" whiteSpace="nowrap">{row.th1}</Th>
              <Th fontSize="xs" whiteSpace="nowrap">{row.th2}</Th>
              <Th fontSize="xs" whiteSpace="nowrap">{row.th3}</Th>
              <Th fontSize="xs" whiteSpace="nowrap">{row.th4}</Th>
              <Th fontSize="xs" whiteSpace="nowrap">{row.th5}</Th>
              <Th fontSize="xs" whiteSpace="nowrap">{row.th6}</Th>
              <Th fontSize="xs" whiteSpace="nowrap">{row.th7}</Th>
              <Th fontSize="xs" whiteSpace="nowrap">{row.th8}</Th>
              <Th fontSize="xs" whiteSpace="nowrap">{row.th9}</Th>
              <Th fontSize="xs" whiteSpace="nowrap">{row.th10}</Th>
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {TbodyData.map((row, index) => (
            <Tr key={index}>
              <Td fontSize="xs" whiteSpace="nowrap">{formatDateString(row.week_ending_date)}</Td>
              <Td fontSize="xs" whiteSpace="nowrap">{row.po_code}</Td>
              <Td fontSize="xs" whiteSpace="nowrap">{row.site_code}</Td>
              <Td fontSize="xs" whiteSpace="nowrap">{row.site_description}</Td>
              <Td fontSize="xs" whiteSpace="nowrap">{row.article_code}</Td>
              <Td fontSize="xs" whiteSpace="nowrap">{row.article_description}</Td>
              <Td fontSize="xs" whiteSpace="nowrap">{row.order_qty}</Td>
              <Td fontSize="xs" whiteSpace="nowrap">{row.receipt_qty}</Td>
              <Td fontSize="xs" whiteSpace="nowrap">{row.strike_rate_percentage}</Td>
              <Td fontSize="xs" whiteSpace="nowrap" p={0}>
                <Select size="xs" defaultValue={row.reason_uuid} onChange={(e) => handleReasonChange(e, row)}>
                  {ddStrikeRate.map((item) => (
                    <option key={item.uuid} value={item.uuid}>
                      {item.text}
                    </option>
                  ))}
                </Select>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

export default TableComment;
